import React, { useState, useEffect } from 'react';
import { getCurrencySettings } from '../../../services/migration/companyDataService';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  Paper,
  CircularProgress,
  TextField,
  IconButton,
  MenuItem,
  Select,
  Alert,
  FormControl,
  InputLabel,
  InputAdornment,
  Snackbar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GuideButton from '../../common/guide/GuideButton';
import { useAuth } from '../../../contexts/AuthContext';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';
import ProjectSelector from '../shared/ProjectSelector';
import { AppDatePicker } from '../../common/AppDatePicker';

interface Currency {
  id: string;
  name: string;
  symbol: string;
  code: string;
  isDefault?: boolean;
}

interface DebitAccountModalProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
  accountName: string;
  companyId: string | null;
  onSuccess?: () => void;
  /** İşlemi yapan ekranın şubesi (manager panelinde kilitli şube) */
  branchId?: string | null;
  /** true ise şube seçimi kilitlidir (manager paneli) */
  branchLocked?: boolean;
}

const DebitAccountModal: React.FC<DebitAccountModalProps> = ({
  open,
  onClose,
  accountId,
  accountName,
  companyId,
  onSuccess,
  branchId = null,
  branchLocked = false
}) => {
  const { userDetails, branches } = useAuth();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState('');
  // PYS: hareketi bir projeye bağlar (opsiyonel)
  const [projectId, setProjectId] = useState<number | null>(null);
  const [documentNo, setDocumentNo] = useState<string>('');
  const [accountBalance, setAccountBalance] = useState<number>(0);
  // Şube seçimi ('' = Genel; manager panelinde kilitli)
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');
  const [transactionDate, setTransactionDate] = useState<string>(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  });

  // Otomatik belge numarası: BRC-YYYYMMDD-HHMMSS (Borçlandırma)
  const generateDocumentNo = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `BRC-${year}${month}${day}-${hours}${minutes}${seconds}`;
  };

  useEffect(() => {
    if (open) {
      setError(null);
      setSuccess(false);
      setSuccessMessage('');
      setSelectedCurrency(null);
      setAmount('');
      setDescription('');
      setProjectId(null);
      setSelectedBranchId(branchId || ''); // Şube: prop'tan gelen değerle başlat
      setDocumentNo(generateDocumentNo());
      const t = new Date();
      setTransactionDate(`${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`);
      loadCurrencies();
      if (companyId && accountId) {
        loadAccountDetails();
      }
    }
  }, [open, companyId, accountId]);

  const getCurrencySymbol = (code: string): string => {
    switch (code) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CHF': return 'Fr.';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      case 'CNY': return '¥';
      default: return code;
    }
  };

  const getCurrencyName = (code: string): string => {
    switch (code) {
      case 'TRY': return tt('Türk Lirası', 'Turkish Lira');
      case 'USD': return tt('Amerikan Doları', 'US Dollar');
      case 'EUR': return tt('Euro', 'Euro');
      case 'GBP': return tt('İngiliz Sterlini', 'British Pound');
      case 'JPY': return tt('Japon Yeni', 'Japanese Yen');
      case 'CHF': return tt('İsviçre Frangı', 'Swiss Franc');
      case 'AUD': return tt('Avustralya Doları', 'Australian Dollar');
      case 'CAD': return tt('Kanada Doları', 'Canadian Dollar');
      case 'CNY': return tt('Çin Yuanı', 'Chinese Yuan');
      default: return code;
    }
  };

  const loadCurrencies = async () => {
    try {
      setLoading(true);
      setError(null);

      const currencyData = await getCurrencySettings(companyId!);
      const settings: { [code: string]: boolean } = { TRY: true };
      if (currencyData) {
        Object.entries(currencyData).forEach(([code, active]: [string, any]) => {
          settings[code] = Boolean(active);
        });
      }

      const availableCurrencies: string[] = [
        'TRY', 'USD', 'EUR', 'GBP', 'CHF', 'JPY', 'AUD', 'CAD', 'RUB', 'AED', 'SAR', 'NOK', 'DKK', 'SEK'
      ];
      const activeCurrencyCodes = availableCurrencies.filter(code => settings[code]);
      const currenciesData: Currency[] = activeCurrencyCodes.map(code => ({
        id: code,
        name: getCurrencyName(code),
        symbol: getCurrencySymbol(code),
        code: code,
        isDefault: code === 'TRY'
      }));

      setCurrencies(currenciesData);
      const defaultCurrency = currenciesData.find(c => c.isDefault === true);
      if (defaultCurrency) {
        setSelectedCurrency(defaultCurrency);
      } else if (currenciesData.length > 0) {
        setSelectedCurrency(currenciesData[0]);
      }
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      setError(tt('Para birimleri yüklenirken hata oluştu: ', 'An error occurred while loading currencies: ') + err.message);
    }
  };

  const loadAccountDetails = async () => {
    try {
      if (!companyId) return;
      const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_ID(parseInt(accountId)));
      const result = response.data;
      const accountData = result.data || result;
      if (accountData) {
        const creditBalance = accountData.creditBalance !== undefined ? accountData.creditBalance : (accountData.balance || 0);
        const debitBalance = accountData.debitBalance || 0;
        setAccountBalance(creditBalance - debitBalance);
      } else {
        setAccountBalance(0);
      }
    } catch (err: any) {
      // Bakiye gösterimi opsiyonel; hata olsa da modalı bloklamayalım
      setAccountBalance(0);
    }
  };

  const formatCurrency = (value: number, currencyCode: string = 'TRY') => {
    return new Intl.NumberFormat(localeCode, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatTurkishNumber = (value: string): string => {
    if (!value) return '';
    const cleanValue = value.replace(/[^0-9,]/g, '');
    const parts = cleanValue.split(',');
    const integerPart = parts[0];
    const decimalPart = parts[1] || '';
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const limitedDecimal = decimalPart.slice(0, 2);
    if (cleanValue.includes(',')) {
      return `${formattedInteger},${limitedDecimal}`;
    }
    return formattedInteger;
  };

  const parseFormattedNumber = (value: string): number => {
    if (!value) return 0;
    const cleanValue = value.replace(/\./g, '').replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value && !/^[0-9.,]*$/.test(value)) return;
    if ((value.match(/,/g) || []).length > 1) return;
    if (value.includes(',')) {
      const parts = value.split(',');
      if (parts[1] && parts[1].length > 2) return;
    }
    setAmount(formatTurkishNumber(value));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!companyId || !accountId || !selectedCurrency) {
        setError(tt('Gerekli bilgiler eksik.', 'Required information is missing.'));
        setLoading(false);
        return;
      }

      const amountValue = parseFormattedNumber(amount);
      if (isNaN(amountValue) || amountValue <= 0) {
        setError(tt('Geçerli bir miktar giriniz.', 'Please enter a valid amount.'));
        setLoading(false);
        return;
      }

      if (!documentNo || documentNo.trim() === '') {
        setError(tt('Belge numarası zorunludur. Lütfen belge numarası giriniz.', 'Document number is required. Please enter a document number.'));
        setLoading(false);
        return;
      }

      const txDateParts = transactionDate.split('-');
      if (txDateParts.length !== 3) {
        setError(tt('Geçerli bir işlem tarihi seçiniz.', 'Please select a valid transaction date.'));
        setLoading(false);
        return;
      }
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const selected = new Date(Number(txDateParts[0]), Number(txDateParts[1]) - 1, Number(txDateParts[2]));
      if (isNaN(selected.getTime())) {
        setError(tt('Geçerli bir işlem tarihi seçiniz.', 'Please select a valid transaction date.'));
        setLoading(false);
        return;
      }
      const diffDays = Math.round((selected.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (Math.abs(diffDays) > 30) {
        const human = selected.toLocaleDateString(localeCode);
        const isBackdated = diffDays < 0;
        const msg = isBackdated
          ? tt(`Seçtiğiniz işlem tarihi (${human}) bugünden ${Math.abs(diffDays)} gün öncedir. Devam etmek istediğinize emin misiniz?`, `The selected transaction date (${human}) is ${Math.abs(diffDays)} days before today. Are you sure you want to continue?`)
          : tt(`Seçtiğiniz işlem tarihi (${human}) bugünden ${diffDays} gün sonradır. Devam etmek istediğinize emin misiniz?`, `The selected transaction date (${human}) is ${diffDays} days after today. Are you sure you want to continue?`);
        const ok = window.confirm(msg);
        if (!ok) {
          setLoading(false);
          return;
        }
      }
      const finalDate = (diffDays === 0)
        ? new Date()
        : new Date(selected.getFullYear(), selected.getMonth(), selected.getDate(), 12, 0, 0);

      // ✅ Tarihi Türkiye yerel saatiyle (timezone kaymadan) gönder.
      // toISOString() UTC'ye çevirip 3 saat geriye kaydırıyordu; backend UTC+3 yerel saat
      // konvansiyonu kullandığı için hareketler 3 saat geride kaydolup listede aşağı düşüyordu.
      const pad2 = (n: number) => String(n).padStart(2, '0');
      const finalDateIso = `${finalDate.getFullYear()}-${pad2(finalDate.getMonth() + 1)}-${pad2(finalDate.getDate())}T${pad2(finalDate.getHours())}:${pad2(finalDate.getMinutes())}:${pad2(finalDate.getSeconds())}`;

      const payload = {
        companyId: companyId,
        accountId: parseInt(accountId),
        accountName: accountName,
        amount: amountValue,
        currency: selectedCurrency.code,
        description: description || tt(`${accountName} carisi borçlandırma (tahakkuk)`, `Debit (accrual) for ${accountName}`),
        documentNo: documentNo.trim(),
        date: finalDateIso, // ✅ Türkiye yerel saati (geri/ileri tarihli işlem desteği)
        createdBy: userDetails?.email || tt('Sistem', 'System'),
        // Şube: gönderilmezse backend cari hesabın şubesine düşer
        branchId: selectedBranchId || null,
        // PYS: proje bağı (null → projesiz)
        projectId: projectId,
      };

      const response = await accountingApi.post(ACCOUNTING_API.TRANSACTION_DEBIT, payload);
      console.log('Borçlandırma işlemi başarılı:', response.data);

      setSuccess(true);
      setSuccessMessage(tt('Cari başarıyla borçlandırıldı!', 'Account has been debited successfully!'));

      setTimeout(() => {
        setSuccess(false);
        if (onSuccess) onSuccess();
        onClose();
      }, 1500);

      setLoading(false);
    } catch (err: any) {
      console.error('Borçlandırma kaydedilemedi:', err);
      setLoading(false);
      const backendMsg = err?.response?.data?.message;
      setError(tt('Borçlandırma kaydedilemedi: ', 'Debit could not be saved: ') + (backendMsg || err.message));
    }
  };

  const handleCloseModal = () => {
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? undefined : handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{tt('Cariyi Borçlandır', 'Debit Current Account')}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GuideButton guideKey="accounts.debit" color="inherit" disabled={loading} />
              <IconButton edge="end" color="inherit" onClick={handleCloseModal} disabled={loading}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {success ? (
            <Box textAlign="center" p={3}>
              <CheckCircleOutlineIcon color="success" style={{ fontSize: 60 }} />
              <Typography variant="h6" color="success" gutterBottom sx={{ mt: 2 }}>
                {successMessage}
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Cari Hesap Bilgisi */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {tt('Cari Hesap', 'Current Account')}
                </Typography>
                <Typography variant="h6" gutterBottom>{accountName}</Typography>
                <Typography
                  variant="body1"
                  color={accountBalance >= 0 ? 'success.main' : 'error.main'}
                  fontWeight="bold"
                >
                  {tt('Bakiye', 'Balance')}: {formatCurrency(accountBalance)}
                </Typography>
              </Paper>

              <Alert severity="info" sx={{ mb: 2 }}>
                {tt('Kasa/banka bakiyesi değişmez; yalnız cariye borç yazılır.', 'Cashbox/bank balance does not change; only a debit is recorded on the account.')}
              </Alert>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Para Birimi */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="currency-select-label">{tt('Para Birimi *', 'Currency *')}</InputLabel>
                <Select
                  labelId="currency-select-label"
                  value={selectedCurrency?.code || ''}
                  label={tt('Para Birimi *', 'Currency *')}
                  onChange={(e) => {
                    const currency = currencies.find(c => c.code === e.target.value);
                    setSelectedCurrency(currency || null);
                  }}
                  disabled={loading}
                >
                  {currencies.map(currency => (
                    <MenuItem key={currency.id} value={currency.code}>
                      {currency.name} ({currency.symbol})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Şube Seçimi */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="branch-select-label">{tt('Şube', 'Branch')}</InputLabel>
                <Select
                  labelId="branch-select-label"
                  value={selectedBranchId}
                  label={tt('Şube', 'Branch')}
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                  disabled={loading || branchLocked}
                >
                  <MenuItem value="">
                    <em>{tt('Genel (cari şubesi)', 'General (account branch)')}</em>
                  </MenuItem>
                  {branches.map(branch => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* İşlem Tarihi */}
              <AppDatePicker
                label={tt('İşlem Tarihi *', 'Transaction Date *')}
                value={transactionDate}
                onChange={setTransactionDate}
                fullWidth
                size="medium"
                sx={{ mb: 2 }}
                required
                disabled={loading}
                helperText={tt('İleri/geri tarihli işlem yapabilirsiniz; varsayılan olarak bugündür.', 'You can record back-dated or future transactions; defaults to today.')}
              />

              {/* Belge Numarası */}
              <TextField
                label={tt('Belge Numarası *', 'Document Number *')}
                value={documentNo}
                onChange={(e) => setDocumentNo(e.target.value)}
                fullWidth
                placeholder={tt('Örn: BRC-2024-001', 'Ex: BRC-2024-001')}
                sx={{ mb: 2 }}
                required
                disabled={loading}
                helperText={tt('Borçlandırma kaydı için belge numarası giriniz', 'Enter a document number for the debit record')}
              />

              {/* Miktar */}
              <TextField
                label={tt('Miktar *', 'Amount *')}
                value={amount}
                onChange={handleAmountChange}
                fullWidth
                type="text"
                placeholder="0,00"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: selectedCurrency && (
                    <InputAdornment position="start">
                      {selectedCurrency.symbol}
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  inputMode: 'decimal',
                  autoComplete: 'off',
                }}
                required
                disabled={loading}
              />

              {/* Açıklama */}
              <TextField
                label={tt('Açıklama (Opsiyonel)', 'Description (Optional)')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder={tt('Borçlandırma ile ilgili açıklama...', 'Description about the debit...')}
                disabled={loading}
              />

              {/* Proje bağı (PYS) */}
              <ProjectSelector value={projectId} onChange={setProjectId} disabled={loading} size="medium" />
            </Box>
          )}
        </DialogContent>

        {!success && (
          <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
            <Button onClick={handleCloseModal} disabled={loading}>
              {tt('İptal', 'Cancel')}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleSubmit}
              disabled={loading || !selectedCurrency || !amount || !documentNo || !transactionDate}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? tt('İşleniyor...', 'Processing...') : tt('Cariyi Borçlandır', 'Debit Account')}
            </Button>
          </DialogActions>
        )}
      </Dialog>


      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        message={successMessage}
      />
    </>
  );
};

export default DebitAccountModal;
